import { AlertTriangle, X } from 'lucide-react'
import { viewerApi } from '@viewer/lib/viewerApi'
import type { IdsReport } from './lib/idsEngine'
import type { IdsTotals } from './useIdsValidation'

// Componentes visuais partilhados do relatório IDS: a barra-resumo (contagens
// + "Ver no 3D" + exportações) e a lista de especificações com as falhas
// clicáveis. Usados tanto pelo painel flutuante (IdsPanel) como pela página do
// módulo (IdsWorkspacePage) para não duplicar a apresentação.

/** Os modelos mudaram desde a última validação: o relatório que estava
 *  em memória descrevia outro IFC e foi limpo. Mais vale dizê-lo do que
 *  deixar o ecrã vazio sem explicação - mesmo aviso da Tabela. Serve as
 *  três superfícies (painel flutuante, página, regras), por isso vive
 *  aqui com o resto do que é partilhado. */
export function IdsStaleNotice({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-[var(--color-border)] bg-[var(--color-accent-bg)] px-3 py-2 text-[11px] text-[var(--color-text)]">
      <AlertTriangle size={14} className="shrink-0 text-[var(--color-accent)]" />
      <span className="flex-1 leading-snug">
        Os modelos mudaram, por isso o relatório foi limpo. Valida outra vez
        para o ter do modelo que está carregado agora.
      </span>
      <button
        type="button"
        onClick={onDismiss}
        title="Fechar aviso"
        className="shrink-0 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
      >
        <X size={12} />
      </button>
    </div>
  )
}

export function IdsSummaryBar({
  totals,
  show3d,
  setShow3d,
  onExportCsv,
  onExportBcf,
  bcfEnabled,
}: {
  totals: IdsTotals
  show3d: boolean
  setShow3d: (on: boolean) => void
  onExportCsv: () => void
  onExportBcf: () => void
  bcfEnabled: boolean
}) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-[var(--color-border)] px-3 py-1.5 text-[11px]">
      <span className="text-[var(--color-ok-text)]">{totals.ok} conformes</span>
      <span className="text-[var(--color-error-text)]">{totals.failing} com falhas</span>
      {totals.unresolved > 0 && (
        <span className="text-[var(--color-warn-text)]">
          {totals.unresolved} não avaliadas
        </span>
      )}
      <label
        className="ml-auto flex cursor-pointer items-center gap-1 text-[var(--color-text-muted)]"
        title="Pinta no 3D os elementos aplicáveis: verdes passam, vermelhos falham."
      >
        <input
          type="checkbox"
          checked={show3d}
          onChange={(e) => setShow3d(e.target.checked)}
          className="accent-[var(--color-accent)]"
        />
        Ver no 3D
      </label>
      <button
        type="button"
        onClick={onExportCsv}
        className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
      >
        Exportar CSV
      </button>
      {bcfEnabled && totals.failing > 0 && (
        <button
          type="button"
          onClick={onExportBcf}
          className="rounded border border-[var(--color-border)] px-1.5 py-0.5 hover:text-[var(--color-text)]"
          title="Cria um BCF novo com um tópico por especificação com falhas (elementos e razões na descrição). Abre depois o módulo BCF para os rever e capturar viewpoints."
        >
          Exportar para BCF
        </button>
      )}
    </div>
  )
}

export function IdsSpecList({
  report,
  modelId,
  expanded,
  toggleSpec,
}: {
  report: IdsReport
  modelId: string
  expanded: Set<number>
  toggleSpec: (i: number) => void
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
      {report.results.map((r, i) => {
        const badge =
          r.status === 'ok'
            ? 'text-[var(--color-ok-text)]'
            : r.status === 'has-failures'
              ? 'text-[var(--color-error-text)]'
              : 'text-[var(--color-warn-text)]'
        const isOpen = expanded.has(i)
        return (
          <div
            key={i}
            className="mb-1 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40"
          >
            <button
              type="button"
              onClick={() => toggleSpec(i)}
              className="flex w-full items-center gap-2 px-2 py-1 text-left text-xs"
            >
              <span className={badge}>
                {r.status === 'ok'
                  ? '✓'
                  : r.status === 'has-failures'
                    ? '✗'
                    : '–'}
              </span>
              <span className="min-w-0 flex-1 truncate text-[var(--color-text)]">
                {r.spec.name ?? '(spec sem nome)'}
              </span>
              {r.status !== 'unresolved' && (
                <span className="shrink-0 font-mono text-[10px] text-[var(--color-text-dim)]">
                  {r.passCount}/{r.applicableCount}
                </span>
              )}
            </button>
            {isOpen && (
              <div className="border-t border-[var(--color-border)]/40 px-2 py-1 text-[11px]">
                {r.status === 'unresolved' && (
                  <p className="text-[var(--color-warn-text)]/80">{r.note}</p>
                )}
                {r.status !== 'unresolved' && r.applicableCount === 0 && (
                  <p className="italic text-[var(--color-text-dim)]">
                    Nenhum elemento do modelo se aplica a esta spec.
                  </p>
                )}
                {r.unsupportedRequirements.length > 0 && (
                  <p className="mb-1 text-[var(--color-warn-text)]/70">
                    Requisitos não avaliados nesta versão:{' '}
                    {r.unsupportedRequirements.join(', ')}.
                  </p>
                )}
                {r.failures.slice(0, 200).map((f) => (
                  <button
                    key={f.expressID}
                    type="button"
                    onClick={() =>
                      void viewerApi.selectByLocalId(
                        modelId,
                        f.expressID,
                        true,
                      )
                    }
                    className="block w-full rounded px-1 py-0.5 text-left hover:bg-[var(--color-bg)]"
                    title={f.reasons.join(' · ')}
                  >
                    <span className="text-[var(--color-text-muted)]">
                      {f.ifcClass}
                      {f.name ? ` · ${f.name}` : ''}
                    </span>
                    <span className="ml-1 text-[var(--color-error-text)]/90">
                      {f.reasons[0]}
                      {f.reasons.length > 1
                        ? ` (+${f.reasons.length - 1})`
                        : ''}
                    </span>
                  </button>
                ))}
                {r.failures.length > 200 && (
                  <p className="text-[var(--color-text-dim)]">
                    … e mais {r.failures.length - 200} (ver CSV).
                  </p>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
